import { isWithinInterval, parseISO } from 'date-fns';

import { ActionType, Query } from '../../design-system/types';

import { isLastMessageFromTraveller } from '../dossier/operations';
import { DisplayDossierOverviews } from '../dossier/types';
import { UserId } from '../login/types';

const filterDossierOverviewsByRating =
    (rating?: string | Array<string> | null) => (dossierOverviews: DisplayDossierOverviews) => {
        if (!rating) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossierOverview) => rating && rating.includes(dossierOverview.rating.toString()),
        );
    };
const filterDossierOverviewsByNumberOfPax =
    (query: Query) => (dossierOverviews: DisplayDossierOverviews) => {
        if (!query.solo && !query.group) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossier) =>
                (query.solo === '1' && dossier.project.paxNumber === 1) ||
                (query.group === '1' &&
                    typeof dossier.project.paxNumber === 'number' &&
                    dossier.project.paxNumber >= 6),
        );
    };
const isDateInRange = (date: Date | null | undefined, from: string, to: string): boolean => {
    if (!date) {
        return false;
    }
    return isWithinInterval(date, { start: parseISO(from), end: parseISO(to) });
};
const filterDossierOverviewsByDepartureDate =
    (from?: string | null, to?: string | null) => (dossierOverviews: DisplayDossierOverviews) => {
        if (!from || !to) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossier) =>
                from &&
                to &&
                isDateInRange(
                    dossier.project.arrivalDate || dossier.project.approximateArrivalDate,
                    from,
                    to,
                ),
        );
    };

const filterDossierOverviewsByRequestDate =
    (from?: string | null, to?: string | null) => (dossierOverviews: DisplayDossierOverviews) => {
        if (!from || !to) {
            return dossierOverviews;
        }

        return dossierOverviews.filter(
            (dossier) => from && to && isDateInRange(dossier.requestDate, from, to),
        );
    };

const filterDossierOverviewsByActionType =
    (actionType?: ActionType[] | null) =>
    (dossierOverviews: DisplayDossierOverviews): DisplayDossierOverviews => {
        if (!actionType) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossierOverview) => actionType && actionType.includes(dossierOverview.task.actionType),
        );
    };
const filterDossierOverviewsByMarket =
    (market?: string | string[] | null) => (dossierOverviews: DisplayDossierOverviews) => {
        if (!market) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossierOverview) => market && market.includes(dossierOverview.project.marketCode),
        );
    };
const filterDossierOverviewsByDestination =
    (destinationLabel?: string | string[] | null) =>
    (dossierOverviews: DisplayDossierOverviews) => {
        if (!destinationLabel) {
            return dossierOverviews;
        }
        return dossierOverviews.filter(
            (dossierOverview) =>
                destinationLabel &&
                destinationLabel.includes(dossierOverview.project.destinationLabel),
        );
    };
const filterDossierOverviewsByBudget =
    (min?: string | string[] | null, max?: string | string[] | null) =>
    (dossierOverviews: DisplayDossierOverviews) => {
        if (
            !min ||
            !max ||
            (min === '0' && max === '15000') ||
            typeof min === 'object' ||
            typeof max === 'object'
        ) {
            return dossierOverviews;
        }

        return dossierOverviews.filter(
            (dossierOverview) =>
                min &&
                max &&
                dossierOverview.project.idealBudget &&
                dossierOverview.project.idealBudget.totalInEuro <= parseInt(max, 10) &&
                dossierOverview.project.idealBudget.totalInEuro >= parseInt(min, 10),
        );
    };

const filterDossierOverviewsByTravellerMessage =
    (filterByTA?: string | string[] | null) => (dossierOverviews: DisplayDossierOverviews) => {
        if (filterByTA !== '1') {
            return dossierOverviews;
        }

        return dossierOverviews.filter(isLastMessageFromTraveller);
    };

const filterByAgentId = (agentId: string | string[], dossierOverviews: DisplayDossierOverviews) =>
    dossierOverviews.filter((dossierOverview) =>
        agentId.includes(dossierOverview.agentId.toString()),
    );

const filterDossierOverviewsByAgentId =
    (userId: UserId, agentId?: string | string[] | null) =>
    (dossierOverviews: DisplayDossierOverviews) => {
        if (!agentId && userId) {
            return filterByAgentId(userId.toString(), dossierOverviews);
        }
        if (agentId && agentId !== 'all') {
            return filterByAgentId(agentId, dossierOverviews);
        }
        return dossierOverviews;
    };

export const filterDossiersByQuery =
    (query: Query, userId: UserId) =>
    (dossierOverviews: DisplayDossierOverviews): DisplayDossierOverviews => {
        const filters = [
            filterDossierOverviewsByAgentId(userId, query.agentId),
            filterDossierOverviewsByRating(query.rating),
            filterDossierOverviewsByNumberOfPax(query),
            filterDossierOverviewsByDepartureDate(
                query.arrivalDateFrom as string,
                query.arrivalDateTo as string,
            ),
            filterDossierOverviewsByRequestDate(
                query.requestDateFrom as string,
                query.requestDateTo as string,
            ),
            filterDossierOverviewsByActionType(query.actionType as ActionType[]),
            filterDossierOverviewsByMarket(query.market),
            filterDossierOverviewsByDestination(query.destination),
            filterDossierOverviewsByBudget(query.minBudget, query.maxBudget),
            filterDossierOverviewsByTravellerMessage(query.TA),
        ];

        return filters.reduce((result, filter) => filter(result), dossierOverviews);
    };
